import React, { useEffect } from 'react';
import styled from 'styled-components';
import { LayoutColors } from '@src/core/theme';
import { buyerSelector } from '@src/features/auth';
import { useWidgetId } from '@src/core/IdProvider';
import { classNamespaceFactory } from '@src/common';
import { orderSaleItems, getOrderAnalyticsData } from '@src/common/order';
import { Sale, Order, OrderStatus, Buyer } from '@src/core/buyerApiClient';
import AuthDisplay, { LOGIN } from '@src/components/Auth/AuthDisplay';
import { CartDisplay } from '@src/components/Checkout/CartDisplay';
import StepNavigationIcon from '@src/components/Product/StepNavigationIcon';
import { useAppSelector, useAppDispatch, useWidgetParam } from '@src/core/hooks';
import { BuyerInfoCollect } from '@src/components/BuyerInfoCollect/BuyerInfoCollect';
import { setStepCheckout, stepCheckoutSelector, checkoutStepState } from '@src/features/checkout';
import {
  loadReturnOrder,
  currentOrderSelector,
  hasPendingSaleRequiredFormInputsSelector,
  loadPendingOrders,
  setupCheckout,
} from '@src/features/order';
import { loadReturnSale } from '@src/features/sale';
import { SuccessfulPayment } from './SuccessfulPayment';
import {
  AUTH_MODE,
  BUYER_INFO_EXTRATION_MODE,
  CART_MODE,
  CONFIRM_SALE_MODE,
  PAYMENT_MODE,
  SUCCESSFUL_PAYMENT,
  SALE_MODE,
} from './contants';
import SaleConfirmation from './SaleConfirmation';
import PaymentForm from './PaymentForm';
import SaleDisplay from '../Account/SaleDisplay';
import { track } from '@src/services/analytics';
import { getUrlParams } from '@src/common/getUrlParams';

type CheckoutProps = {
  editable?: boolean;
  skipResumeCart?: boolean;
  skipConfirmation?: boolean;
};

const callParamFunctionAsync = (param: string, order: Order, buyer: Buyer | undefined) => {
  const wdw: any = window;
  const func = wdw[param] ?? null;

  if (func) {
    setTimeout(() => {
      func(createOrderToUseBySellerTemplates(order), buyer);
    }, 0);
  }
};

// this function is used to pass the sale to the outside world taking care of supporting old versions of the API
const createSaleToUseBySellerTemplates = (sale: Sale) => ({
  ...sale,
  total_amount_cents: sale.totalAmountCents,
});

const createOrderToUseBySellerTemplates = (order: Order) => ({
  ...order,
  items: orderSaleItems(order),
  state: order.status,
  total_amount_cents: order.finalAmountCents,
});

const Checkout = (props: CheckoutProps) => {
  const { editable = true, skipResumeCart = false, skipConfirmation = false } = props;

  const onCheckoutInit = useWidgetParam('on-checkout-init');
  const onSaleCompletion = useWidgetParam('on-sale-completion');
  const id = useWidgetId();
  const dispatch = useAppDispatch();
  const [buyer, logged] = useAppSelector(buyerSelector);
  const currentOrder = useAppSelector(currentOrderSelector);
  const pendingFormInputs = useAppSelector(hasPendingSaleRequiredFormInputsSelector);
  const skipResumeCartPage = useWidgetParam('skip-resume-cart-page');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cn = classNamespaceFactory('checkout');
  const stepCheckout = useAppSelector(stepCheckoutSelector(id));
  const { steps, currentStep } = useAppSelector(checkoutStepState(id));

  const initialMode = (skipResumeCartPage && skipResumeCartPage == 'true') || skipResumeCart ? AUTH_MODE : CART_MODE;
  const [mode, setMode] = React.useState(initialMode);

  const decideModeBasedOnSale = (order: Order) => {
    if (pendingFormInputs) {
      setMode(BUYER_INFO_EXTRATION_MODE);
    } else if (order.status === OrderStatus.Confirmed) {
      // setMode(SUCCESSFUL_PAYMENT);
      setMode(SALE_MODE);

      track('checkoutCompleted', getOrderAnalyticsData(currentOrder!));
    } else {
      setMode(PAYMENT_MODE);
    }
  };

  useEffect(() => {
    const orderId = getUrlParams('orderId');

    if (orderId && buyer) {
      dispatch(setupCheckout(orderId));
    }
  }, [buyer]);

  useEffect(() => {
    dispatch(setStepCheckout({ id, value: '' }));

    switch (mode) {
      case PAYMENT_MODE:
        if (onCheckoutInit) {
          callParamFunctionAsync(onCheckoutInit, currentOrder!, buyer);
        }

        break;
      case SALE_MODE:
      case SUCCESSFUL_PAYMENT:
        if (onSaleCompletion) {
          callParamFunctionAsync(onSaleCompletion, currentOrder!, buyer);
        }

        break;
      case AUTH_MODE:
        dispatch(setStepCheckout({ id, value: LOGIN }));
        break;
      default:
    }
  }, [mode]);

  useEffect(() => {
    if (logged) {
      dispatch(loadPendingOrders());
      dispatch(loadReturnSale());
      dispatch(loadReturnOrder());
    }
  }, [logged]);

  useEffect(() => {
    if (currentOrder) {
      track('checkoutInitiated', getOrderAnalyticsData(currentOrder));
    }
  }, []);

  useEffect(() => {
    if (currentOrder) {
      decideModeBasedOnSale(currentOrder);
    }
  }, [currentOrder]);

  const onLogin = () => {
    if (currentOrder) {
      decideModeBasedOnSale(currentOrder);
    } else {
      setMode(skipConfirmation ? PAYMENT_MODE : CONFIRM_SALE_MODE);
    }
  };

  return (
    <div>
      {stepCheckout ? (
        <NavigationSteps>
          {steps.map((element, index) => {
            return (
              <StepNavigationIcon
                key={index}
                number={element}
                stepType='checkout'
                type={stepCheckout}
                isActive={currentStep?.order === element}
                mainColor={LayoutColors.justBlue}
                reverseColor={true}
              />
            );
          })}
        </NavigationSteps>
      ) : null}
      {(() => {
        switch (mode) {
          case CART_MODE:
            return <CartDisplay {...{ cn }} proceedToNextStage={() => setMode(AUTH_MODE)} editable={editable} />;
          case AUTH_MODE:
            return <AuthDisplay {...{ onLogin }} hasBackAction />;
          case CONFIRM_SALE_MODE:
            return <SaleConfirmation />;
          case PAYMENT_MODE:
            return <PaymentForm {...{ cn }} order={currentOrder} />;
          case BUYER_INFO_EXTRATION_MODE:
            return <BuyerInfoCollect proceedToNextStage={() => decideModeBasedOnSale(currentOrder!)} />;
          case SUCCESSFUL_PAYMENT:
            return <SuccessfulPayment />;
          case SALE_MODE:
            return (
              <SaleDisplayStyleWrapper>
                <SaleDisplay order={currentOrder!} />
              </SaleDisplayStyleWrapper>
            );
          default:
            return null;
        }
      })()}
    </div>
  );
};

const NavigationSteps = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 26px;
`;

const SaleDisplayStyleWrapper = styled.div`
  margin: 20px auto;
  border: 1px solid #949494;
  max-width: 800px;
  padding: 10px;
  overflow: hidden;
  background-color: #fff;
  color: #444;
  border-radius: 10px;
`;

export default Checkout;
